import type { BadgeVariant } from 'components/partials/badge/badge';

import type { CurrentUserData } from 'hooks/api/use-current-user';

import Icons from 'assets/icons';

import { canAccessCriticalPopulations } from 'helpers/critical-populations-access';

export type NavItemBadgePopover = 'monitoring-intro';

export interface NavItemBadge {
  text: string;
  variant?: BadgeVariant;
  className?: string;
  popover?: NavItemBadgePopover;
}

const MONITORING_NEW_BADGE: NavItemBadge = {
  text: 'New',
  variant: 'critical',
  popover: 'monitoring-intro',
};

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: React.ReactNode;
  match?: string;
  exactMatch?: boolean;
  badge?: NavItemBadge;
  notificationType?: string;
  isVisible?: (
    user?: Record<string, unknown>,
    featureFlags?: Record<string, boolean>
  ) => boolean;
  children?: NavItem[];
}

export const adminItems: NavItem[] = [
  {
    id: 'clients',
    label: 'Clients',
    to: '/admin/clients',
    icon: <Icons.Clients className="size-5" />,
    match: '/admin/clients',
    exactMatch: true,
  },
  {
    id: 'all-users',
    label: 'All users',
    to: '/admin/users',
    icon: <Icons.Users className="size-5" />,
    match: '/admin/users',
  },
  {
    id: 'admin-reports',
    label: 'Reports',
    to: '/admin/reports',
    icon: <Icons.Reports className="size-5" />,
    match: '/admin/reports',
    isVisible: (user) => !user?.internalAdmin,
  },
  {
    id: 'admin-products',
    label: 'Products',
    to: '/admin/products',
    icon: <Icons.Product className="size-5" />,
    match: '/admin/products',
    isVisible: (user) => !user?.internalAdmin,
  },
  {
    id: 'admin-global-settings',
    label: 'Global settings',
    to: '/admin/global-settings',
    icon: <Icons.Gear className="size-5" />,
    match: '/admin/global-settings',
    isVisible: (user) => !!(user?.superAdmin || user?.reportAdmin),
  },
];

export const getClientItems = (
  clientId: string | number,
  currentYear: number,
  featureFlags: Record<string, boolean> = {},
  user: CurrentUserData | null | undefined = undefined
): NavItem[] => [
  {
    id: 'client-users',
    label: 'Users',
    to: `/admin/clients/${clientId}/users`,
    icon: <Icons.Users className="size-5" />,
    match: `/admin/clients/${clientId}/users`,
  },
  {
    id: 'client-files',
    label: 'Files',
    to: `/admin/clients/${clientId}/files`,
    icon: <Icons.MyFiles className="size-5" />,
    match: `/admin/clients/${clientId}/files`,
  },
  {
    id: 'client-insights',
    label: 'Insights',
    to: `/admin/clients/${clientId}/insights`,
    icon: <Icons.Idea className="size-5" />,
    match: `/admin/clients/${clientId}/insights`,
  },
  {
    id: 'client-goals',
    label: 'Goals',
    to: `/admin/clients/${clientId}/goals/${currentYear}`,
    icon: <Icons.Target className="size-5" />,
    match: `/admin/clients/${clientId}/goals`,
  },
  {
    id: 'client-critical-populations',
    label: 'Critical Elements',
    to: `/admin/clients/${clientId}/critical-populations`,
    icon: <Icons.Puzzle className="size-5" />,
    match: `/admin/clients/${clientId}/critical-populations`,
    isVisible: () => canAccessCriticalPopulations(user, featureFlags),
  },
  {
    id: 'client-monitoring',
    label: 'Monitoring',
    to: `/admin/clients/${clientId}/monitoring`,
    icon: <Icons.Monitoring className="size-5" />,
    match: `/admin/clients/${clientId}/monitoring`,
    isVisible: () => featureFlags['critical-elements-monitoring'],
    badge: MONITORING_NEW_BADGE,
  },
  {
    id: 'client-products',
    label: 'Products',
    to: `/admin/clients/${clientId}/products`,
    icon: <Icons.Product className="size-5" />,
    match: `/admin/clients/${clientId}/products`,
  },
  {
    id: 'client-reports',
    label: 'Reports',
    to: `/admin/clients/${clientId}/reports`,
    icon: <Icons.Reports className="size-5" />,
    match: `/admin/clients/${clientId}/reports`,
  },
  {
    id: 'client-aid-studio',
    label: 'Aid Studio',
    to: `/admin/clients/${clientId}/aid-studio`,
    icon: <Icons.AidStudio className="size-5" />,
    match: `/admin/clients/${clientId}/aid-studio`,
    isVisible: () => featureFlags['tab-fao'],
  },
  {
    id: 'client-profile',
    label: 'Profile',
    to: `/admin/clients/${clientId}/profile`,
    icon: <Icons.Settings className="size-5" />,
    match: `/admin/clients/${clientId}/profile`,
  },
  {
    id: 'ai-assistant',
    label: 'Ask MARK',
    to: `/admin/clients/${clientId}/ai-assistant`,
    icon: <Icons.MarkIcon className="size-5" />,
    match: `/admin/clients/${clientId}/ai-assistant`,
    isVisible: () => featureFlags['ai-assistant'],
  },
];

export const getPortalItems = (
  featureFlags: Record<string, boolean> = {}
): NavItem[] => [
  {
    id: 'home',
    label: 'Home',
    to: '/home',
    icon: <Icons.Home className="size-5" />,
    match: '/home',
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    to: '/monitoring',
    icon: <Icons.Monitoring className="size-5" />,
    match: '/monitoring',
    isVisible: () => featureFlags['critical-elements-monitoring'],
    badge: MONITORING_NEW_BADGE,
  },
  {
    id: 'reports',
    to: '/reports',
    label: 'Reports',
    icon: <Icons.Reports className="size-5" />,
    match: '/reports',
    children: [
      {
        id: 'all-reports',
        label: 'All Reports',
        to: '/reports/all',
        icon: <Icons.AllReports className="size-5" />,
        match: '/reports/all',
      },
      {
        id: 'favorites',
        label: 'Favorites',
        to: '/reports/favorites',
        icon: <Icons.Star className="size-5" />,
        match: '/reports/favorites',
      },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    to: '/insights',
    icon: <Icons.Idea className="size-5" />,
    match: '/insights',
    isVisible: () => featureFlags['tab-insights'],
    notificationType: 'insights',
  },
  {
    id: 'learn',
    label: 'Learn',
    to: '/learn',
    icon: <Icons.Help className="size-5" />,
    match: '/learn',
    children: [
      {
        id: 'learn-hub',
        label: 'Learning Hub',
        to: '/learn',
        icon: <Icons.Learn className="size-5" />,
        match: '/learn',
        exactMatch: true,
      },
      {
        id: 'learn-courses',
        label: 'Courses',
        to: '/learn/courses',
        icon: <Icons.Courses className="size-5" />,
        match: '/learn/courses',
        isVisible: () => featureFlags.courses !== false,
      },
      {
        id: 'learn-articles',
        label: 'Articles',
        to: '/learn/content/articles',
        icon: <Icons.Articles className="size-5" />,
        match: '/learn/content/articles',
        isVisible: () => featureFlags.articles,
      },
      {
        id: 'learn-guides',
        label: 'Guides',
        to: '/learn/content/guides',
        icon: <Icons.Guides className="size-5" />,
        match: '/learn/content/guides',
        isVisible: () => featureFlags.guides !== false,
      },
      {
        id: 'learn-downloads',
        label: 'Downloads',
        to: '/learn/content/downloads',
        icon: <Icons.Downloads className="size-5" />,
        match: '/learn/content/downloads',
        isVisible: () => featureFlags.downloads !== false,
      },
      {
        id: 'learn-webinars',
        label: 'Webinars',
        to: '/learn/content/webinars',
        icon: <Icons.Webinars className="size-5" />,
        match: '/learn/content/webinars',
        isVisible: () => featureFlags.webinars !== false,
      },
      {
        id: 'learn-announcements',
        label: 'Product Announcements',
        to: '/learn/content/product-announcements',
        icon: <Icons.ProductAnnouncements className="size-5" />,
        match: '/learn/content/product-announcements',
        isVisible: () => featureFlags.releaseNotes !== false,
      },
    ],
  },
  {
    id: 'files',
    label: 'Files',
    to: '/files',
    icon: <Icons.MyFiles className="size-5" />,
    match: '/files',
    notificationType: 'files',
  },
  {
    id: 'aid-studio',
    label: 'Aid Studio',
    to: '/aid-studio',
    icon: <Icons.AidStudio className="size-5" />,
    match: '/aid-studio',
    isVisible: () => featureFlags['tab-fao'],
  },
  {
    id: 'ai-assistant',
    label: 'Ask MARK',
    to: '/ai-assistant',
    icon: <Icons.MarkIcon className="size-5" />,
    match: '/ai-assistant',
    isVisible: () => featureFlags['ai-assistant'],
  },
];
